// Legacy: getYoutubeEmbedUrl() duplicated across architect/modern/minimal templates.
// Security hardening (not in legacy): only youtube.com / youtu.be / youtube-nocookie.com
// hosts are ever turned into an iframe src, and the embed always uses the
// nocookie domain to avoid third-party tracking cookies on public portfolios.

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

const VIDEO_ID_PATTERN =
  /^.*(?:youtu\.be\/|\/v\/|\/u\/\w\/|\/embed\/|watch\?v=|&v=)([^#&?]*).*/;

/**
 * Returns a safe `https://www.youtube-nocookie.com/embed/<id>` URL when `url`
 * points at a recognized YouTube host and contains a valid 11-char video id.
 * Returns `null` for anything else (including non-YouTube hosts), so callers
 * never render an `<iframe>` for arbitrary/untrusted URLs.
 */
export function getYoutubeEmbedUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  let normalized = String(url).trim();
  if (!/^https?:\/\//i.test(normalized) && !normalized.startsWith('//')) {
    normalized = `https://${normalized}`;
  }

  let hostname: string;
  try {
    hostname = new URL(normalized).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!YOUTUBE_HOSTS.has(hostname)) {
    return null;
  }

  const match = normalized.match(VIDEO_ID_PATTERN);
  const videoId = match && match[1];
  if (!videoId || videoId.length !== 11) {
    return null;
  }

  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

export function isYoutubeLink(url: string | null | undefined): boolean {
  return getYoutubeEmbedUrl(url) !== null;
}
